import * as _ from 'lodash';
import moment from 'moment';

import type { IssueCount } from '../../../shared/types/routes/issueCounts';
import type { Point } from '../generic/Graphs/Graph';

/**
 * Generates the list of points for the graph.  This function generates one point for each day between the start and end
 * dates.  It fills in any missing dates with the previous snapshot values.
 *
 * The algorithm works by keeping an index into the items array and a value for the current date.  Then it loops and on each iteration it does two things:
 * 1. Advances the index until it's found the last item before the end of the current day.
 * 2. Creates points for each day until the next item is on the next day.
 *
 * This loop handles all sorts of cases like:
 * 1. Items don't start until the middle of the range
 * 2. Items end before the end of the range
 * 3. Items are missing for some days
 * 4. There are multiple items on the same day
 * 5. The first snapshot is before the start date and needs to be used to fill
 */
export function generatePoints(startDate: Date, endDate: Date, items: IssueCount[]): Point[] {
  // Abort if no snapshots.
  if (_.isEmpty(items)) {
    return [];
  }

  const start = moment(startDate);
  const end = moment.min(moment().endOf('day'), moment(endDate));

  // Abort if the start date is in the future.
  if (start.isAfter(moment())) {
    return [];
  }

  let nextItemIndex = 0;
  let currItem = { active: 0, ignored: 0, remediated: 0, date: start.toISOString() };
  let nextItem = items[nextItemIndex];
  const currDay = start.clone().startOf('day');
  const endOfDay = currDay.clone().add(1, 'day');

  const points: Point[] = [];

  while (currDay.isSameOrBefore(end)) {
    // Advance through snapshots on this day to select the last one for the current day or run out of items.
    while (nextItem && endOfDay.isAfter(nextItem.date)) {
      currItem = nextItem;
      nextItemIndex++;
      nextItem = items[nextItemIndex];
    }

    // Add entries for each day up until the next snapshot
    while (
      (
        !nextItem // If there are no more items, just keep filling in with the last current data.
        // This was previously `isBefore`, but that caused an issue with specifically UTC+01:00 timezone (London).
        // This was changed to `isSameOrBefore` to fix the issue and seems to be fine in general, but it's worth keeping an eye on.
        || endOfDay.isSameOrBefore(nextItem.date) // Otherwise, fill until we reach the day of the next item
      )
      && currDay.isSameOrBefore(end) // Make sure we never go past the end date.
    ) {
      points.push({
        name: currDay.format('MMM DD'),
        data: _.pick<IssueCount, 'active' | 'ignored' | 'remediated'>(currItem, ['active', 'ignored', 'remediated']),
      });

      currDay.add(1, 'day');
      endOfDay.add(1, 'day');
    }
  }

  return points;
}
